import { gql, useQuery } from "@apollo/client";
import cn from "classnames";
import React, { useEffect, useState } from "react";
import { LogOut } from "react-feather";
import { useDispatch } from "react-redux";
import {
  matchPath,
  NavLink,
  Redirect,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import { animated, useTransition } from "react-spring";
import { useIntercom } from "react-use-intercom";
import Logo from "../../components/Logo";
import * as r from "../../routes";
import { logout } from "../../store/modules/user/actions";
import Annuity from "./steps/Annuity";
import Contact from "./steps/Contact";
import Financials from "./steps/Financial";
import Plans from "./steps/Plans";
import Final from "./steps/Summary";

const Onboarding = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [availableSteps, setAvailableSteps] = useState<string[]>([]);
  const { update } = useIntercom();

  const { data, loading } = useQuery(gql`
    {
      self {
        uuid
        firstName
        lastName
        email
      }
    }
  `);

  useEffect(() => {
    if (!loading && data) {
      update({
        userId: data.self.uuid,
        email: data.self.email,
        name: `${data.self.firstName} ${data.self.lastName}`.trim(),
      });
    }
  }, [loading, data, update]);

  useEffect(() => {
    if (!!matchPath(location.pathname, r.ROUTE_ONBOARDING_PLANS)) {
      setAvailableSteps([r.ROUTE_ONBOARDING_PLANS]);
    } else if (!!matchPath(location.pathname, r.ROUTE_ONBOARDING_CONTACT)) {
      setAvailableSteps([r.ROUTE_ONBOARDING_PLANS, r.ROUTE_ONBOARDING_CONTACT]);
    } else if (!!matchPath(location.pathname, r.ROUTE_ONBOARDING_FINANCIAL)) {
      setAvailableSteps([
        r.ROUTE_ONBOARDING_PLANS,
        r.ROUTE_ONBOARDING_CONTACT,
        r.ROUTE_ONBOARDING_FINANCIAL,
      ]);
    } else if (!!matchPath(location.pathname, r.ROUTE_ONBOARDING_ANNUITY)) {
      setAvailableSteps([
        r.ROUTE_ONBOARDING_PLANS,
        r.ROUTE_ONBOARDING_CONTACT,
        r.ROUTE_ONBOARDING_FINANCIAL,
        r.ROUTE_ONBOARDING_ANNUITY,
      ]);
    } else if (!!matchPath(location.pathname, r.ROUTE_ONBOARDING_SUMMARY)) {
      setAvailableSteps([
        r.ROUTE_ONBOARDING_PLANS,
        r.ROUTE_ONBOARDING_CONTACT,
        r.ROUTE_ONBOARDING_FINANCIAL,
        r.ROUTE_ONBOARDING_ANNUITY,
        r.ROUTE_ONBOARDING_SUMMARY,
      ]);
    }
  }, [location.pathname]);

  const transitions = useTransition(location, {
    key: location.pathname,
    from: { opacity: 0, transform: "translate3d(100%,0,0)" },
    enter: { opacity: 1, transform: "translate3d(0%,0,0)" },
    leave: { opacity: 0, transform: "translate3d(-50%,0,0)" },
  });

  return (
    <div className="flex flex-col h-full w-full min-h-0 min-w-0 bg-gray-50">
      <div className="flex-none flex items-center px-8 h-14">
        <div className="flex w-60">
          <Logo type="plain" size={32} className="w-auto" />
          <div className="border-l border-gray-300 h-8 mx-4"></div>
          <div className="text-xl font-bold">Onboarding</div>
        </div>
        <div className="flex-auto h-full flex justify-center items-center">
          <Step
            number="1"
            title="Plans"
            path={r.ROUTE_ONBOARDING_PLANS}
            availableSteps={availableSteps}
          />
          <Step
            number="2"
            title="Contact Information"
            path={r.ROUTE_ONBOARDING_CONTACT}
            availableSteps={availableSteps}
          />
          <Step
            number="3"
            title="Financial Background"
            path={r.ROUTE_ONBOARDING_FINANCIAL}
            availableSteps={availableSteps}
          />
          <Step
            number="4"
            title="Annuity"
            path={r.ROUTE_ONBOARDING_ANNUITY}
            availableSteps={availableSteps}
          />
          <Step
            number="5"
            title="Summary"
            path={r.ROUTE_ONBOARDING_SUMMARY}
            availableSteps={availableSteps}
          />
        </div>
        <div className="ml-auto w-60 flex justify-end">
          <button
            className="flex items-center text-gray-600 focus:outline-none outline-none"
            onClick={() => {
              dispatch(logout());
            }}
          >
            <LogOut />
            <div className="ml-2">Logout</div>
          </button>
        </div>
      </div>
      <div
        id="onboarding-container"
        className="flex-auto relative min-h-0 overflow-auto flex flex-col"
      >
        {transitions((props, loc) => {
          return (
            <animated.div
              style={props as any}
              className="flex-auto flex justify-center min-h-0 absolute w-full h-full"
            >
              <Switch location={loc}>
                <Route path={r.ROUTE_ONBOARDING_PLANS}>
                  <Plans />
                </Route>
                <Route path={r.ROUTE_ONBOARDING_CONTACT}>
                  <Contact />
                </Route>
                <Route path={r.ROUTE_ONBOARDING_FINANCIAL}>
                  <Financials />
                </Route>
                <Route path={r.ROUTE_ONBOARDING_ANNUITY}>
                  <Annuity />
                </Route>
                <Route path={r.ROUTE_ONBOARDING_SUMMARY}>
                  <Final />
                </Route>
                <Redirect to={r.ROUTE_ONBOARDING_PLANS} />
              </Switch>
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};

const Step = ({
  number,
  title,
  path,
  availableSteps,
}: {
  number: string;
  title: string;
  path: string;
  availableSteps: string[];
}) => {
  const location = useLocation();
  const active = !!matchPath(location.pathname, path);

  return (
    <NavLink
      to={path}
      className={cn(
        "mx-2 px-3 relative h-full flex items-center text-sm font-bold",
        active ? "text-primary" : "text-gray-400",
        availableSteps.includes(path) ? "cursor-pointer" : "cursor-not-allowed"
      )}
      onClick={(e) => {
        if (!availableSteps.includes(path)) {
          e.preventDefault();
        }
      }}
    >
      <div
        className={cn(
          "h-6 w-6 rounded-full flex items-center justify-center border mr-2",
          active ? "border-primary bg-primary text-white" : "border-gray-400"
        )}
      >
        {number}
      </div>
      {title}
    </NavLink>
  );
};

export default Onboarding;
